import React, { useRef } from "react";
import { Divider, Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  referenceTootKit,
  getTextContentHeading,
  sectionStyle,
  h1style,
  h2style,
  h3style,
  referenceHtml,
  paraStyle,
  DashedBorderStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import chamberImg from "../../../assets/Images/Chamber PP and GP Images/ppiRoom.png";
import { FigureDesc55 } from "../../../utils/tmp/ImageDescription/ChamberAssistantID";

const referenceStyle = {
  color: "black",
  fontFamily: "GT Super Text",
  fontSize: " 24px",
  fontWeight: "700",
  letterSpacing: "0px",
  textAlign: "left",
  fontStyle: "italic",
  cursor: "pointer",
  marginBottom: "10px",
};

const ChamberAssistant = ({ goToSection }) => {
  const referencesRef = useRef(null);

  const handleScrollToReferences = () => {
    if (referencesRef.current) {
      referencesRef.current.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };
  const reference = (_data, number) => {
    return referenceTootKit(_data, number, handleScrollToReferences);
  };

  const referenceData = [
    {
      content:
        "Separate spaces must be designated for the PP and GP offices. This Handbook recommends similar design considerations for both the spaces.",
    },
  ];

  const ObjectiveObject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Design the Chambers to{" "}
        <span style={{ color: "#DC002D" }}>streamline processes </span> for the
        effective execution of legal proceedings;
      </>,

      <>
        Ensure
        <span style={{ color: "#DC002D" }}>
          {" "}
          swift response, efficient coordination, and confidentiality
        </span>
        , when necessary, by locating the PP/GP Chambers to be in close
        proximity to Courtrooms;
      </>,

      <>
        Ensure that the
        <span style={{ color: "#DC002D" }}>
          {" "}
          office spaces are accessible to diverse user groups{" "}
        </span>{" "}
        of lawyers, litigants and staff who may use the space;
      </>,

      <>
        Provide a spacious, private waiting area prioritising the{" "}
        <span style={{ color: "#DC002D" }}>
          {" "}
          comfort and privacy of all litigants{" "}
        </span>
        visiting the PP/GP Chambers; and
      </>,

      <>
        Ensure provision of
        <span style={{ color: "#DC002D" }}>
          {" "}
          adequate, accessible sanitary provisions{" "}
        </span>
        for diverse user groups.
      </>,
    ],
    isOrdered: false,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Public Prosecutor/Government Pleader",
      "Assistant Public Prosecutor/Assistant Government Pleader (where applicable)",
      "Empanelled Advocates",
      "Office staff",
      "Litigants",
    ],
    isOrdered: false,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The Chambers for the PP/GP must be strategically located near or in close proximity to the Courtrooms, or have segregated access to the Courtrooms in order to ensure direct access whenever necessary. ",

      "Office spaces for empanelled lawyers and staff must be in close proximity to the Chambers for the PP/GP, to ensure seamless coordination and efficient functioning. ",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The Chambers for the PP/GP must be designed to be a self-contained space for the officers, their staff, and other empanelled lawyers. ",

        "A designated waiting area must be provided at the entrance of the Chambers, or in close proximity, to ensure that visiting litigants have a designated seating space. ",

        "Adequate designated space must be provided within the Chambers or in close proximity for office staff and other empanelled lawyers. ",

        "The offices must have dedicated accessible sanitary provisions, or be in close proximity to the same. ",

        "All spaces in the Chambers must be equipped with modular seating, and provision should be made for natural light and ventilation, including well-designed acoustics. ",

        "The Chambers must have adequate compact storage for all the case files being dealt with.",
      ],
    },
    {
      heading: "Offices of the PP/GP",
      items: [
        "Separate cubicles must be provided for the (Assistant) PP and the (Assistant) GP, wherever possible. ",

        "The rooms must be designed to have adequate work space, as well as storage for the files and personal belongings of the PP/GP. ",

        "The rooms must have adequate ergonomic seating for litigants visiting the space. ",

        <>
          The rooms must be designed to ensure private conversations with
          effective acoustic barriers, including soundproof partitions, acoustic
          doors, acoustically absorptive ceiling, and wall materials. For
          further details on acoustics, please refer to{" "}
          <span
            style={sectionStyle}
            onClick={() => goToSection("annexes", "HeadingAnnexure5")}
          >
            Annexure 5.
          </span>
        </>,
      ],
    },
    {
      heading: "Waiting Area",
      items: [
        "Adequate space must be provided for a separate, dedicated waiting area for litigants visiting the PP/GP. ",

        "The waiting area must be accessible to diverse user groups with adequate ramps and elevators, accessible signage, and tactile paths. ",

        <>
          Provision must be made for accessible case display screens and
          information kiosks with audible announcements. For further details on
          the design of the waiting area, please refer to
          <span
            onClick={() => goToSection("waitingArea", "headingWaitingAreas")}
            style={sectionStyle}
          >
            {" "}
            Section 2.2.
          </span>
        </>,
      ],
    },
    {
      heading: "Space for Office Clerks/Secretaries",
      items: [
        "Adequate space must be provided for the offices of the secretarial staff attached with each PP/GP. ",

        "Adequate ergonomic seating and technology must be provided, for the various functions to be undertaken by the staff.",

        "Adequate storage facilities must be provided to store official documents as well as personal belonging of the staff.",
      ],
    },
    {
      heading: "Space for Empanelled Advocates",
      items: [
        <>
          Separate workspace must be provided for empanelled Advocates. For
          further details on design of a multi-person office space, refer to
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
            style={sectionStyle}
          >
            {" "}
            Section 2.5.C.
          </span>
        </>,

        "These rooms must be equipped with a case display system (with audible announcements) and kiosks, to ensure that the Advocates are able to access information regarding ongoing cases conveniently.",

        "Adequate functional work spaces, with ergonomic furniture must be provided for the Advocates and other staff to undertake their work. ",
      ],
    },
    {
      heading: "Library",
      items: [
        "The Chambers of the PP/GP must be equipped with requisite dedicated library facilities. ",

        "The library must be well-equipped with IT infrastructure, to ensure smooth functioning and access to sufficient resources for research and legal reference.",

        <>
          The library must have sufficient provision of ergonomic chairs, desks,
          storage cupboards, planned electrical points, fans, etc., For further
          details on the design of the library, please refer to
          <span
            onClick={() => goToSection("library", "headingLibrary")}
            style={sectionStyle}
          >
            {" "}
            Section 2.9.C.
          </span>
        </>,
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 style={h1style} id="partC">
          PART C - THE HANDBOOK
        </h1>
        <h2 style={h2style} id="organizationHeading">
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANISATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="facilitiesForAdvocatesHeading" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.6. </span>
          Facilities for Advocates
        </h3>
        <h4 id="chambersHeading" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.6.</span>B. Chambers for the
          Public Prosecutors and Government Pleaders
        </h4>
        <p style={paraStyle}>
          While Court Complexes are not obligated to provide dedicated space for
          a separate Bar Association building, ideally space must be provided to
          set up rooms within the Court Complex for empanelled Advocates,
          including government pleaders and public prosecutors.
        </p>

        <p style={paraStyle}>
          (Assistant) Public Prosecutors (“PP”) and (Assistant) Government
          Pleaders (“GP”) are officers of the Court who are responsible for
          undertaking proceedings on behalf of the State. The spaces they occupy
          play a pivotal role in ensuring seamless coordination and effective
          legal representation.
        </p>

        <p style={paraStyle}>
          Adequate space must therefore be provided for the PP/GP, office spaces
          for other empanelled lawyers, their staff, and the litigants and
          general public visiting their offices.
          {reference(referenceData[0], 1)} The design of these Chambers must
          provide adequate space for all legal professionals and litigants,
          ensuring coordination, privacy, and ease of access.
        </p>
        {getTextContentHeading(ObjectiveObject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}

        <CaptionWithBorders1
          image={[chamberImg]}
          isZoom={true}
          caption={"Figure 55: Typical floor plan for Chambers of the PP/GP"}
          zoomImgStyle={{ height: "600px" }}
          imageDescription={FigureDesc55}
        />
        <h2 ref={referencesRef} id="chamberAssistantRef" style={referenceStyle}>
          References
          <Divider />
        </h2>
        {referenceHtml(referenceData)}
      </Typography>
    </div>
  );
};

export default ChamberAssistant;
